import { useEffect, useState } from 'react';
import { useContext } from 'react';

import { trackPageViews } from '@/common/analytics/gtm/trackPageViews';
import { optimoveSetPageVisit } from '@/common/analytics/optimove/optimoveSetPageVisit';
import { optimoveSetUserId } from '@/common/analytics/optimove/optimoveSetUserId';
import { AthenaContext, AthenaClickEventType } from '@/common/hooks/useAthena/athenaEvents';
import { useAthena } from '@/common/hooks/useAthena/useAthena';
import { useClientStack } from '@/common/hooks/useClientStack/useClientStack';
import { useOptimove } from '@/common/hooks/useOptimove/useOptimove';
import { AuthContext } from '@/contexts/auth/AuthContext';

interface TrackProps {
  pageCategory: string;
  athenaContext?: AthenaContext;
  additionalData?: Record<string, unknown>;
  shouldClearDataLayer?: boolean;
}

/*
Track events are pushed into queue. They are then send to athena when optimove is initialized and user status has been checked. 
*/
export const useTracking = () => {
  const [trackQueue, setTrackQueue] = useState<TrackProps[]>([]);
  const { consumeClientStackItems: consumeSessionStackItems } = useClientStack();
  const { optimoveSDKInitialized } = useOptimove();
  const { fetchedUserInfo, userInfo, error } = useContext(AuthContext);

  const { pushEvent } = useAthena();

  const track = ({ pageCategory, athenaContext, additionalData = {} }: TrackProps) => {
    if (
      !['category', 'search', 'collection-page', 'payment', 'checkout'].includes(pageCategory) &&
      athenaContext
    ) {
      pushEvent({
        eventType: 'view',
        eventName: (pageCategory as AthenaClickEventType) || 'view',
        context: athenaContext,
      });
    }
    trackPageViews(pageCategory, additionalData, userInfo);
    consumeSessionStackItems([
      'trackRegistrationComplete',
      'trackUserLogout',
      'trackUserLogin',
      'login',
    ]);

    // OPTIMOVE
    optimoveSetPageVisit(pageCategory);
    optimoveSetUserId(userInfo?.customerId);
  };

  const addTracking = (trackProps: TrackProps) => {
    setTrackQueue((prevQueue) => [...prevQueue, trackProps]);
  };

  useEffect(() => {
    // TODO: https://displate.atlassian.net/browse/EFT-1179
    if (optimoveSDKInitialized && trackQueue.length && (fetchedUserInfo || error)) {
      trackQueue.forEach((trackProps) => {
        track(trackProps);
      });
      setTrackQueue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackQueue.length, optimoveSDKInitialized, fetchedUserInfo, error]);

  return {
    track: addTracking,
  };
};
